import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Button — v2 design-system primary control.
 *
 * Strategic role of each variant (see Agentlets-Design-Principles.md §4.1):
 *   - primary    : deep teal CTA. The default action. NOT brand red.
 *   - secondary  : white surface with subtle border. Companion / cancel.
 *   - ghost      : transparent, text-only. Tertiary / inline.
 *   - danger     : brand red (#c53030). Reserved for kill-switch, destructive
 *                  irreversible actions, BLOCK confirmations. Withholding red
 *                  is what gives it meaning — do not use for ordinary deletes.
 *
 * Sizes:
 *   - sm : compact (in-row actions, dense tables)
 *   - md : default
 *   - lg : prominent CTAs (page hero, modals)
 *
 * Accessibility:
 *   - aria-busy when loading; the click handler is suppressed.
 *   - aria-disabled when disabled.
 *   - Visible focus ring via .v2-focus-ring (defined in v2-tokens.css).
 *   - Icons get aria-hidden so the text label is the screen-reader source.
 *
 * Composition:
 *   <Button variant="primary" iconLeft={<Plus/>}>New Agentlet</Button>
 *   <Button variant="secondary" loading>Saving…</Button>
 *   <Button variant="danger" onClick={confirmKill}>Engage Kill Switch</Button>
 *
 * @param {Object}    props
 * @param {React.ReactNode} props.children
 * @param {'primary'|'secondary'|'ghost'|'danger'} [props.variant='primary']
 * @param {'sm'|'md'|'lg'}                          [props.size='md']
 * @param {React.ReactNode}                         [props.iconLeft]
 * @param {React.ReactNode}                         [props.iconRight]
 * @param {boolean}                                 [props.loading=false]
 * @param {boolean}                                 [props.disabled=false]
 * @param {boolean}                                 [props.fullWidth=false]
 * @param {'button'|'submit'|'reset'}               [props.type='button']
 * @param {(e: React.MouseEvent) => void}           [props.onClick]
 * @param {string}                                  [props.className]
 * @param {string}                                  [props.ariaLabel]
 *   Additional `<button>` props (e.g. data-* attributes) are forwarded.
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  iconLeft,
  iconRight,
  loading = false,
  disabled = false,
  fullWidth = false,
  type = 'button',
  onClick,
  className = '',
  ariaLabel,
  ...rest
}) {
  const isDisabled = disabled || loading;

  const base =
    'v2-focus-ring inline-flex items-center justify-center gap-2 ' +
    'font-medium select-none whitespace-nowrap ' +
    'transition-[background-color,border-color,color,box-shadow,transform] ' +
    'duration-v2 ease-v2 ' +
    'disabled:cursor-not-allowed disabled:opacity-60';

  /* G378 — `xs` size: 17 in-slideout/in-card callsites passed size="xs"
   * which fell through to `undefined`, dropping all sizing AND rounding so
   * the buttons rendered as sharp rectangles with no padding. Adding the
   * missing size keeps every caller round-cornered and consistently sized.
   *
   * Also bumped sm/md/lg from rounded-v2-sm → rounded-v2-md so the entire
   * button family reads as the "pill-soft" shape the V2 design system
   * favours over the harder small-radius rectangle. */
  const sizeClass = {
    xs: 'text-[11.5px] px-2.5 py-1 rounded-v2-md h-6',
    sm: 'text-[12px] px-3 py-1.5 rounded-v2-md h-7',
    md: 'text-[12.5px] px-3.5 py-2 rounded-v2-md h-8',
    lg: 'text-[14px] px-5 py-2.5 rounded-v2-md h-10 font-semibold',
  }[size];

  const variantClass = {
    primary:
      'bg-v2-action border border-v2-action text-white ' +
      'shadow-v2-xs ' +
      'hover:bg-v2-action-strong hover:border-v2-action-strong ' +
      'active:translate-y-px',
    secondary:
      'bg-v2-surface border border-v2-border text-v2-ink ' +
      'hover:bg-v2-muted hover:border-v2-border-strong ' +
      'active:translate-y-px',
    ghost:
      'bg-transparent border border-transparent text-v2-ink-secondary ' +
      'hover:bg-v2-muted hover:text-v2-ink',
    danger:
      'bg-v2-brand border border-v2-brand text-white ' +
      'shadow-v2-xs ' +
      'hover:bg-v2-brand-strong hover:border-v2-brand-strong ' +
      'active:translate-y-px',
  }[variant];

  const widthClass = fullWidth ? 'w-full' : '';

  // Icon-only spinner for loading state — prefer leading-icon slot, fall
  // through to a standalone spinner if neither icon slot is provided.
  /* G378 — Icon size now keys off the size token (xs:10, sm:12, md/lg:14). */
  const iconPx = size === 'xs' ? 10 : size === 'sm' ? 12 : 14;
  const spinner = (
    <Loader2
      aria-hidden="true"
      className="animate-spin"
      style={{ width: iconPx, height: iconPx }}
    />
  );

  const renderIcon = (slot, fallback) => {
    if (loading && slot === 'left' && !iconLeft && !iconRight) return spinner;
    if (loading && slot === 'left' && iconLeft) return spinner;
    if (loading && slot === 'right' && !iconLeft && iconRight) return spinner;
    if (slot === 'left' && iconLeft) {
      return (
        <span aria-hidden="true" className="flex items-center" style={{ width: iconPx, height: iconPx }}>
          {iconLeft}
        </span>
      );
    }
    if (slot === 'right' && iconRight) {
      return (
        <span aria-hidden="true" className="flex items-center" style={{ width: iconPx, height: iconPx }}>
          {iconRight}
        </span>
      );
    }
    return null;
  };

  return (
    <button
      type={type}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      aria-label={ariaLabel}
      className={`${base} ${sizeClass} ${variantClass} ${widthClass} ${className}`.trim()}
      {...rest}
    >
      {renderIcon('left')}
      <span>{children}</span>
      {renderIcon('right')}
    </button>
  );
}

export default Button;
